<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import AgentCell from './AgentCell.vue';

// --- ADDED FOR LAST SEEN ---
// Import Chatwoot's time helper to format the Unix timestamp
import { dynamicTime } from 'shared/helpers/timeHelper';
// ---------------------------

const props = defineProps({
  agents: {
    type: Array,
    default: () => [],
  },
  agentMetrics: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  agentLastSeen: {
    type: Object,
    default: () => ({}),
  },
});
// ---------------------------

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

// UI Settings key for agent table page size
const AGENT_TABLE_PAGE_SIZE_KEY = 'report_overview_agent_table_page_size';
const ONLINE_ACTIVE_WINDOW_SECONDS = 2 * 60;

// Get the saved page size from UI settings or default to 10
const getPageSize = () => {
  return uiSettings.value[AGENT_TABLE_PAGE_SIZE_KEY] || 10;
};

const handlePageSizeChange = pageSize => {
  updateUISettings({ [AGENT_TABLE_PAGE_SIZE_KEY]: pageSize });
};

const getAgentMetrics = id =>
  props.agentMetrics.find(metrics => metrics.assignee_id === Number(id)) || {};

const formatRelativeTime = unixTimestamp => {
  if (!unixTimestamp) {
    return null;
  }

  return dynamicTime(Number(unixTimestamp)).toLowerCase();
};

const isRecentlyActive = unixTimestamp => {
  const activeTimestamp = Number(unixTimestamp);

  return (
    Number.isFinite(activeTimestamp) &&
    Math.floor(Date.now() / 1000) - activeTimestamp <=
      ONLINE_ACTIVE_WINDOW_SECONDS
  );
};

const availabilityLabel = status => {
  if (status === 'busy') {
    return t('OVERVIEW_REPORTS.AGENT_STATUS.BUSY');
  }

  if (status === 'offline') {
    return t('OVERVIEW_REPORTS.AGENT_STATUS.OFFLINE');
  }

  return t('OVERVIEW_REPORTS.AGENT_STATUS.ONLINE');
};

const getAvatarStatus = ({ availabilityStatus, effectiveStatus }) => {
  return availabilityStatus === 'online' ? effectiveStatus : availabilityStatus;
};

const buildLastSeenLabel = ({
  availabilityStatus,
  effectiveStatus,
  activeAt,
  statusChangedAt,
}) => {
  if (availabilityStatus === 'busy') {
    const relativeTime = formatRelativeTime(statusChangedAt);
    return relativeTime
      ? t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.BUSY_SINCE', {
          time: relativeTime,
        })
      : availabilityLabel('busy');
  }

  if (availabilityStatus === 'offline') {
    const relativeTime = formatRelativeTime(statusChangedAt);
    return relativeTime
      ? t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.OFFLINE_SINCE', {
          time: relativeTime,
        })
      : availabilityLabel('offline');
  }

  const relativeTime = formatRelativeTime(activeAt);

  if (availabilityStatus === 'online' && isRecentlyActive(activeAt)) {
    return availabilityLabel('online');
  }

  return relativeTime
    ? t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LAST_ACTIVE', {
        time: relativeTime,
      })
    : availabilityLabel(effectiveStatus);
};

const tableData = computed(() =>
  props.agents
    .map(agent => {
      const metric = getAgentMetrics(agent.id);
      const agentPresence = props.agentLastSeen[agent.id] || {};
      const availabilityStatus =
        agentPresence.availability_status || agent.availability_status;
      const effectiveStatus = availabilityStatus;
      const lastSeenFormatted = buildLastSeenLabel({
        availabilityStatus,
        effectiveStatus,
        activeAt: agentPresence.active_at,
        statusChangedAt: agentPresence.status_changed_at,
      });
      const avatarStatus = getAvatarStatus({
        availabilityStatus,
        effectiveStatus,
      });

      return {
        agent: agent.available_name || agent.name,
        email: agent.email,
        thumbnail: agent.thumbnail,
        open: metric.open || 0,
        unattended: metric.unattended || 0,
        status: avatarStatus,
        lastSeen: lastSeenFormatted, // --- ADDED FOR LAST SEEN ---
      };
    })
    .sort((a, b) => {
      // First sort by open tickets (descending)
      const openDiff = b.open - a.open;
      // If open tickets are equal, sort by name (ascending)
      if (openDiff === 0) {
        return a.agent.localeCompare(b.agent);
      }
      return openDiff;
    })
);

const defaulSpanRender = cellProps =>
  h(
    'span',

    {
      class: cellProps.getValue()
        ? 'capitalize text-n-slate-12'
        : 'capitalize text-n-slate-11',
    },
    cellProps.getValue() ? cellProps.getValue() : '---'
  );

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('agent', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AGENT'),
    cell: cellProps => h(AgentCell, cellProps),
    size: 250,
  }),
  columnHelper.accessor('open', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.OPEN'),
    cell: defaulSpanRender,
    size: 100,
  }),
  columnHelper.accessor('unattended', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.UNATTENDED'),
    cell: defaulSpanRender,
    size: 100,
  }),
  // --- ADDED FOR LAST SEEN ---
  // This automatically adds the new column to the TanStack Vue table
  columnHelper.accessor('lastSeen', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.LAST_SEEN'),
    cell: defaulSpanRender,
    size: 120,
  }),
  // ---------------------------
];

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: {
      pageSize: getPageSize(),
    },
  },
});
</script>

<template>
  <div class="flex flex-col flex-1">
    <Table :table="table" class="max-h-[calc(100vh-21.875rem)]" />
    <Pagination
      class="mt-2"
      :table="table"
      show-page-size-selector
      :default-page-size="getPageSize()"
      @page-size-change="handlePageSizeChange"
    />
    <div
      v-if="props.isLoading"
      class="items-center flex text-base justify-center p-8"
    >
      <Spinner />
      <span>
        {{ $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LOADING_MESSAGE') }}
      </span>
    </div>
    <EmptyState
      v-else-if="!props.isLoading && !props.agents.length"
      :title="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.NO_AGENTS')"
    />
  </div>
</template>
